import type { MiningConfigs, MiningGroup } from '../types/mining';
import { generateUUID } from './uuid';
import { Sentry } from './sentry';
import { getAuthState } from '@/contexts/AuthContext';
import { miningApiService } from '@/services/miningApiService';

const MINING_CONFIGS_KEY = 'cryptotrack_mining_configs';

// ──────────────────────────────────────────────────────────────────────────────
// LocalStorage helpers (Guest mode)
// ──────────────────────────────────────────────────────────────────────────────

const loadMiningConfigsFromLocalStorage = (): MiningConfigs => {
  try {
    const stored = localStorage.getItem(MINING_CONFIGS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    Sentry.captureException(error, { tags: { operation: 'loadMiningConfigsFromLocalStorage' } });
    return {};
  }
};

const saveMiningConfigsToLocalStorage = (configs: MiningConfigs): void => {
  try {
    localStorage.setItem(MINING_CONFIGS_KEY, JSON.stringify(configs));
  } catch (error) {
    Sentry.captureException(error, { tags: { operation: 'saveMiningConfigsToLocalStorage' } });
  }
};

// ──────────────────────────────────────────────────────────────────────────────
// Public API — hybrid (API when authenticated, LocalStorage in Guest mode)
// ──────────────────────────────────────────────────────────────────────────────

export const loadMiningConfigs = async (): Promise<MiningConfigs> => {
  const { isAuthenticated } = getAuthState();
  if (isAuthenticated) {
    try {
      return await miningApiService.listAsConfigs();
    } catch (error) {
      Sentry.captureException(error, { tags: { operation: 'loadMiningConfigs_api' }, level: 'warning' });
      return loadMiningConfigsFromLocalStorage();
    }
  }
  return loadMiningConfigsFromLocalStorage();
};

export const saveMiningConfigs = (configs: MiningConfigs): void => {
  // In API mode this is a no-op — mutations go through addMiningGroup / updateMiningGroup / removeMiningGroup
  const { isAuthenticated } = getAuthState();
  if (!isAuthenticated) {
    saveMiningConfigsToLocalStorage(configs);
  }
};

export const addMiningGroup = async (tokenId: string, group: Omit<MiningGroup, 'id'>): Promise<MiningGroup> => {
  const { isAuthenticated } = getAuthState();
  if (isAuthenticated) {
    try {
      return await miningApiService.create(tokenId, group);
    } catch (error) {
      Sentry.captureException(error, { tags: { operation: 'addMiningGroup_api' } });
      throw error;
    }
  }

  const configs = loadMiningConfigsFromLocalStorage();
  const newGroup: MiningGroup = { ...group, id: generateUUID() };
  if (!configs[tokenId]) configs[tokenId] = [];
  configs[tokenId].push(newGroup);
  saveMiningConfigsToLocalStorage(configs);
  return newGroup;
};

export const updateMiningGroup = async (
  tokenId: string,
  groupId: string,
  updatedGroup: Partial<Omit<MiningGroup, 'id'>>
): Promise<void> => {
  const { isAuthenticated } = getAuthState();
  if (isAuthenticated) {
    try {
      await miningApiService.update(groupId, tokenId, updatedGroup);
      return;
    } catch (error) {
      Sentry.captureException(error, { tags: { operation: 'updateMiningGroup_api' } });
      throw error;
    }
  }

  const configs = loadMiningConfigsFromLocalStorage();
  if (configs[tokenId]) {
    const idx = configs[tokenId].findIndex((g) => g.id === groupId);
    if (idx !== -1) {
      configs[tokenId][idx] = { ...configs[tokenId][idx], ...updatedGroup };
      saveMiningConfigsToLocalStorage(configs);
    }
  }
};

export const removeMiningGroup = async (tokenId: string, groupId: string): Promise<void> => {
  const { isAuthenticated } = getAuthState();
  if (isAuthenticated) {
    try {
      await miningApiService.delete(groupId);
      return;
    } catch (error) {
      Sentry.captureException(error, { tags: { operation: 'removeMiningGroup_api' } });
      throw error;
    }
  }

  const configs = loadMiningConfigsFromLocalStorage();
  if (configs[tokenId]) {
    configs[tokenId] = configs[tokenId].filter((g) => g.id !== groupId);
    if (configs[tokenId].length === 0) delete configs[tokenId];
    saveMiningConfigsToLocalStorage(configs);
  }
};

export const getMiningGroupsForToken = async (tokenId: string): Promise<MiningGroup[]> => {
  const { isAuthenticated } = getAuthState();
  if (isAuthenticated) {
    try {
      const configs = await miningApiService.listAsConfigs();
      return configs[tokenId] || [];
    } catch (error) {
      Sentry.captureException(error, { tags: { operation: 'getMiningGroupsForToken_api' }, level: 'warning' });
    }
  }
  const configs = loadMiningConfigsFromLocalStorage();
  return configs[tokenId] || [];
};
